import { useCallback } from 'react';

// INS-1351: the external-recognition public form (public/external-recognition/:publicToken)
// is reached via a shared link / QR code. On a device with the Bonusly mobile app installed,
// that link can be intercepted and rendered inside the app's own in-app webview instead of a
// normal browser tab. That webview has no chrome of its own, so once it's open there was no way
// back to the app short of a force-quit.
//
// We looked for an existing native bridge this app already uses to talk to that webview shell —
// the pattern used in use-user-color-mode.ts, which calls window.webkit.messageHandlers.colorMode
// / window.Android.setStatusBarColor to keep native status-bar chrome in sync — and found no
// equivalent for "dismiss/close the webview". Inventing a new postMessage/URL-scheme protocol here
// would ship a close button that silently does nothing in exactly the case this ticket is about,
// because nothing on the native side would be listening for it. Closing that gap needs a
// coordinated native-app change and is out of scope for a kaleidoscope-only fix.
//
// Because we can't distinguish "normal top-level navigation, no opener" from "opened inside the
// native webview" from script, and because window.close() is a documented no-op (no error, no
// visible side effect) when the page doesn't have permission to close itself, the close button is
// always rendered and always attempts window.close() unconditionally. This closes the window for
// the population that CAN be closed programmatically (windows opened via script), and is inert —
// not misleading — for everyone else, rather than swapping the control for copy ("close this
// tab") that assumes tab chrome the native webview doesn't have.
export const requestCloseWindow = (): void => {
	if (typeof window === 'undefined') return;
	window.close();
};

export interface UseCloseWindowResult {
	// Attempt to close the current window/tab. No-ops silently (per the browser close contract)
	// when the window has no script opener, including inside the native app's in-app webview.
	requestClose: () => void;
}

export const useCloseWindow = (): UseCloseWindowResult => {
	const requestClose = useCallback(() => {
		requestCloseWindow();
	}, []);

	return { requestClose };
};
